import React, { useState } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { MailIcon, ArrowLeftIcon } from 'lucide-react';
import { usePasswordReset } from '../hooks';
import { Button, InputField } from '@shared/components';

interface PasswordResetFormProps {
  onBackToLogin: () => void;
  primaryColor: string;
}

const PasswordResetForm = ({
  onBackToLogin,
  primaryColor
}: PasswordResetFormProps) => {
  const { isDarkMode } = useDarkMode();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const { requestPasswordReset, isLoading, isSubmitted } = usePasswordReset();

  // Validation function
  const validateEmail = () => {
    if (!email) {
      setEmailError('Email is required');
      return false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  // Check if form is valid for button state
  const isFormValid = email && !emailError;

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setEmail(value);

    // Clear error when user starts typing
    if (emailError) {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail()) {
      return;
    }

    await requestPasswordReset(email);
  };

  return (
    <div className="w-full max-w-md">
      <button
        type="button"
        onClick={onBackToLogin}
        className="mb-4 flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-1" />
        Back to login
      </button>
      <h2 className="text-2xl font-bold mb-2 text-center text-gray-900 dark:text-gray-100">
        Reset your password
      </h2>
      {!isSubmitted ? (
        <>
          <p className="text-gray-600 dark:text-gray-300 mb-6 text-center">
            Enter your email address and we'll send you a link to reset your
            password.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <InputField
                label="Email"
                name="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter your email"
                required
                fullWidth
                darkMode={isDarkMode}
                className={emailError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
              />
              {emailError && (
                <p className="text-sm text-red-600 mt-1">{emailError}</p>
              )}
            </div>
            <Button
              type="submit"
              variant="primary"
              disabled={!isFormValid}
              isLoading={isLoading}
              fullWidth
            >
              {isLoading ? 'Sending...' : 'Send reset link'}
            </Button>
          </form>
        </>
      ) : (
        <div className="text-center py-8">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Check your email
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            We've sent a password reset link to{' '}
            <span className="font-medium">{email}</span>
          </p>
          <button
            type="button"
            onClick={onBackToLogin}
            className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            style={!isDarkMode ? { color: primaryColor } : undefined}
          >
            Back to login
          </button>
        </div>
      )}
    </div>
  );
};

export default PasswordResetForm;
